const numberFormatter = new Intl.NumberFormat('en-GB');
const percentFormatter1 = new Intl.NumberFormat('en-GB', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const percentFormatter2 = new Intl.NumberFormat('en-GB', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const dateFormatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'numeric' });
const dateYearFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
});
const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const timeFormatter = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });

export function formatNumber(n: number): string {
  return numberFormatter.format(n);
}

/** `rate` is a fraction (0.012 = 1,2 %). Uses two decimals below 1 % so small rates stay legible. */
export function formatPercent(rate: number): string {
  return (rate < 0.01 ? percentFormatter2 : percentFormatter1).format(rate);
}

export function formatDate(epochMs: number): string {
  return dateFormatter.format(new Date(epochMs));
}

/** Date with the year, for dates that can lie years back (e.g. a contact's last order). */
export function formatDateWithYear(epochMs: number): string {
  return dateYearFormatter.format(new Date(epochMs));
}

export function formatDateTime(epochMs: number): string {
  return dateTimeFormatter.format(new Date(epochMs));
}

export function formatTime(epochMs: number): string {
  return timeFormatter.format(new Date(epochMs));
}

/** The browser's IANA time zone name, e.g. "Europe/Berlin" ("UTC" if it cannot be read). */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/**
 * A send time stated plainly with its zone, e.g. "Thu 8 Oct 2026, 09:00 (Europe/Berlin)".
 * Built from parts so the shape does not depend on the ICU version.
 */
export function formatSendTime(epochMs: number, timeZone: string = browserTimeZone()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(epochMs));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')} ${get('year')}, ${get('hour')}:${get('minute')} (${timeZone})`;
}

export const pad2 = (n: number) => String(n).padStart(2, '0');

/** Epoch ms to the browser-local values of a date input ("2026-10-08") and a time input ("09:00"). */
export function toLocalInputs(epochMs: number): { date: string; time: string } {
  const d = new Date(epochMs);
  return {
    date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
  };
}

/** Browser-local date and time inputs to epoch ms (UTC); `null` while either is empty or invalid. */
export function fromLocalInputs(date: string, time: string): number | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!d || !t) return null;
  const [y, mo, da, h, mi] = [d[1], d[2], d[3], t[1], t[2]].map(Number) as [
    number,
    number,
    number,
    number,
    number,
  ];
  const out = new Date(y, mo - 1, da, h, mi, 0, 0);
  // new Date() rolls 31 Feb over to March; refuse that instead of guessing.
  if (out.getFullYear() !== y || out.getMonth() !== mo - 1 || out.getDate() !== da) return null;
  return out.getTime();
}

const moneyFormatters = new Map<string, Intl.NumberFormat>();

/**
 * Amount in the tenant's reporting currency (`label_settings.currency`, EUR by default),
 * en-GB style, whole units unless `fractionDigits` says otherwise, e.g. (18650, 'EUR') →
 * "€18,650", (1.8, 'EUR', 2) → "€1.80". An unknown currency code falls back to
 * "CODE 18,650" instead of throwing, since the code is admin-editable.
 */
export function formatMoney(
  amount: number,
  currency: string = 'EUR',
  fractionDigits: number = 0,
): string {
  const code = currency.trim().toUpperCase();
  const key = `${code}:${fractionDigits}`;
  let formatter = moneyFormatters.get(key);
  if (!formatter) {
    try {
      formatter = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
    } catch {
      const plain = new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
      return `${code} ${plain.format(amount)}`;
    }
    moneyFormatters.set(key, formatter);
  }
  return formatter.format(amount);
}

/**
 * A sending cost: two decimals, since a campaign often costs only cents
 * ("€1.80"); a cost above 0 that rounds to 0 shows as "< €0.01".
 */
export function formatCost(amount: number, currency: string = 'EUR'): string {
  if (amount > 0 && amount < 0.005) return `< ${formatMoney(0.01, currency, 2)}`;
  return formatMoney(amount, currency, 2);
}

/** The price per 1,000 emails with as many decimals as it has, 2 to 4 ("€0.09", "€0.095"). */
export function formatSendPrice(costPerThousand: number, currency: string = 'EUR'): string {
  let digits = 2;
  while (digits < 4 && Math.abs(Number(costPerThousand.toFixed(digits)) - costPerThousand) > 1e-9) {
    digits += 1;
  }
  return formatMoney(costPerThousand, currency, digits);
}

const roiSmallFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });
const roiFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });

/**
 * ROI as a multiple of the sending cost, e.g. 4166.4 → "4,166×", 3.25 → "3.3×";
 * "–" when nothing was spent (`null`).
 */
export function formatRoi(roi: number | null): string {
  if (roi === null || !Number.isFinite(roi)) return '–';
  return `${(roi < 10 ? roiSmallFormatter : roiFormatter).format(roi)}×`;
}

export function formatRelativeDay(epochMs: number, now: number = Date.now()): string {
  const d1 = new Date(epochMs);
  const d2 = new Date(now);
  const sameDay = d1.toDateString() === d2.toDateString();
  return sameDay ? 'today' : formatDate(epochMs);
}

/** First name guessed from the login email ("mikkel.m@…" → "Mikkel"), for users who have not set a name. */
export function displayName(email: string | null | undefined): string | undefined {
  const first = email?.split('@')[0]?.split(/[._-]/)[0];
  if (!first) return undefined;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

/** The name the admin greets a user by: their own display name, else the one guessed from the email. */
export function userName(
  user: { displayName?: string | null; email?: string | null } | null | undefined,
): string | undefined {
  return user?.displayName || displayName(user?.email);
}

/** "HH:MM" to minutes after midnight (0..1439); `null` while empty or invalid. */
export function timeToMinutes(time: string): number | null {
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!t) return null;
  const h = Number(t[1]);
  const m = Number(t[2]);
  return h < 24 && m < 60 ? h * 60 + m : null;
}
